import React, { useState, useEffect } from 'react';
import { 
  Title, 
  Text, 
  Button, 
  Modal, 
  LoadingOverlay, 
  Group, 
  Stack,
  Paper,
  Collapse,
  Grid,
  Checkbox,
  Divider,
  ColorSwatch
} from '@mantine/core';
import { IconPlus, IconEdit, IconCheck, IconX } from '@tabler/icons-react';
import ServiceForm from '../forms/ServiceForm';
import './ServicesManagementSettings.css';
import { useApp } from '../../context/AppContext';
import { useSalon } from '../../context/SalonContext';
import StaffServiceDetails from './StaffServiceDetails';

function ServicesManagement({ onSubmit }) {
  const { deleteSalonService } = useApp();
  const { employees, serviceTypeOptions, services, setServices, currentUser } = useSalon();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [expandedService, setExpandedService] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [dontAskAgain, setDontAskAgain] = useState(false);

  useEffect(() => {
    if (services && employees && serviceTypeOptions) {
      setIsLoading(false);
    }
    // Check if user has previously set "don't ask again"
    const skipConfirmation = localStorage.getItem('skipServiceDeleteConfirmation') === 'true';
    setDontAskAgain(skipConfirmation);
  }, [services, employees, serviceTypeOptions]);

  const handleAddService = (serviceData) => {
    const newServices = [
      ...services,
      {
        id: services.length + 1,
        ...serviceData
      }
    ];
    setServices(newServices);
    setIsFormOpen(false);
    onSubmit();
  };

  const handleEditService = (serviceData) => {
    setServices(services.map(service => 
      service.id === selectedService.id ? { ...service, ...serviceData } : service
    ));
    setIsEditFormOpen(false);
    setSelectedService(null);
    onSubmit();
  };

  const handleServiceClick = (service) => {
    setExpandedService(expandedService?.id === service.id ? null : service);
  };

  const handleEditClick = (e, service) => {
    e.stopPropagation();
    setSelectedService(service);
    setIsEditFormOpen(true);
  };

  const handleDeleteClick = (e, service) => {
    e.stopPropagation();
    const skipConfirmation = localStorage.getItem('skipServiceDeleteConfirmation') === 'true';
    
    if (skipConfirmation) {
      handleDeleteService(service.id);
    } else {
      setServiceToDelete(service);
      setIsDeleteModalOpen(true);
    }
  };

  const handleDeleteConfirm = () => {
    if (dontAskAgain) {
      localStorage.setItem('skipServiceDeleteConfirmation', 'true');
    }
    handleDeleteService(serviceToDelete.id);
    setIsDeleteModalOpen(false);
    setServiceToDelete(null);
  };

  const handleDeleteService = async (id) => {
    setIsLoading(true);
    const result = await deleteSalonService(id);
    if (result) {
      const remainingServices = services.filter(service => service.id !== id);
      setServices(remainingServices);
    }
    setIsLoading(false);
  };

  // Add this helper function to group services
  const groupServicesByType = (services) => {
    if (!services) {
      return {};
    }

    return services.reduce((groups, service) => {
      const type = service.service_type || 'Other';
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(service);
      return groups;
    }, {});
  };

  return (
    <>
      <LoadingOverlay visible={isLoading} />
      <Stack p="lg">
        <Group justify="space-between">
          <Title order={3}>Salon Services</Title>
          {(currentUser?.role === "Owner" || currentUser?.role === "Manager") && (
            <Button
              leftSection={<IconPlus size="1rem" />}
              color="violet"
              onClick={() => setIsFormOpen(true)}
            >
              Add Service
            </Button>
          )}
        </Group>
        
        <Stack spacing="xl">
          {Object.entries(groupServicesByType(services)).map(([type, typeServices]) => (
            <Stack key={type} spacing="xs">
              <Text fw={600} size="lg" c="dimmed">{type}</Text>
              {typeServices?.map((service) => (
                <Paper 
                  key={service.id}
                  withBorder
                  className={`service-item ${expandedService?.id === service.id ? 'selected' : ''}`}
                  onClick={() => handleServiceClick(service)}
                  radius="md"
                >
                  <Group justify="space-between" p="md">
                    <Group gap="md">
                      <Text fw={500}>{service.name}</Text>
                    </Group>
                    <Group>
                      {(currentUser?.role === "Owner" || currentUser?.role === "Manager") && (
                        <>
                          <Button
                            variant="light"
                            color="violet"
                            size="xs"
                            leftSection={<IconEdit size="1rem" />}
                            onClick={(e) => handleEditClick(e, service)}
                        >
                          Edit
                        </Button>
                        <Button
                            variant="subtle"
                            color="red"
                            size="xs"
                            onClick={(e) => handleDeleteClick(e, service)}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </Group>
                  </Group>

                  <Collapse in={expandedService?.id === service.id} className="service-item">
                    <Divider mx="md"/>
                    <Stack spacing="xs" p="md">
                      <Grid>
                        <Grid.Col span={6}>
                          <Stack gap={0}>
                            <Text fw={600} size="sm">Service Type</Text>
                            <Text size="sm">{service.service_type}</Text>
                          </Stack>
                        </Grid.Col>
                        <Grid.Col span={6}>
                          <Stack gap={0}>
                            <Text fw={600} size="sm">Default Calendar Color</Text>
                            <Group gap="xs">
                              <ColorSwatch 
                                color={service.default_calendar_event_color}
                                size={16}
                                radius="sm"
                              />
                            </Group>
                          </Stack>
                        </Grid.Col>
                        <Grid.Col span={6}>
                          <Stack gap={0}>
                            <Text fw={600} size="sm">Default Duration</Text>
                            <Text size="sm">{service.default_duration_mins} minutes</Text>
                          </Stack>
                        </Grid.Col>
                        <Grid.Col span={6}>
                          <Stack gap={0}>
                            <Text fw={600} size="sm">Default Price</Text>
                            <Text size="sm">${service.default_price}{service.default_is_variable_price ? " and up" : ""}</Text>
                          </Stack>
                        </Grid.Col>
                        <Grid.Col span={6}>
                          <Stack gap={0}>
                            <Text fw={600} size="sm">Description</Text>
                            { service.description ? (
                              <Text size="sm">{service.description}</Text>
                            ) : (
                              <Text size="sm" c="dimmed">No description provided</Text>
                            )}
                          </Stack>
                        </Grid.Col>
                        <Grid.Col span={6}>
                          <Stack gap={0}>
                            <Text fw={600} size="sm">Enable Self-Service</Text>
                            <Text size="sm">{service.default_is_self_servicable ? "Yes" : "No"}</Text>
                          </Stack>
                        </Grid.Col>
                      </Grid>

                      <Stack gap={0} mt="md">
                        <Text fw={600} size="sm">Staff Availability</Text>
                        {employees?.map(employee => (
                          <StaffServiceDetails
                            key={employee.id}
                            employee={employee}
                            service={service}
                            staffService={service.servicing_staff?.find(ss => ss.staff_id === employee.id)}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  </Collapse>
                </Paper>
              ))}
            </Stack>
          ))}
        </Stack>

        <Modal
          opened={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          title="Add New Service"
          size="lg"
        >
          <ServiceForm
            onSubmit={handleAddService}
            onClose={() => setIsFormOpen(false)}
            serviceTypes={serviceTypeOptions}
          />
        </Modal>

        <Modal
          opened={isEditFormOpen}
          onClose={() => {
            setIsEditFormOpen(false);
            setSelectedService(null);
          }}
          title="Edit Service"
          size="lg"
        >
          <ServiceForm
            initialData={selectedService}
            onSubmit={handleEditService}
            onClose={() => {
              setIsEditFormOpen(false);
              setSelectedService(null);
            }}
            isEditing={true}
            serviceTypes={serviceTypeOptions}
          />
        </Modal>

        <Modal
          opened={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setServiceToDelete(null);
          }}
          title="Confirm Delete"
          size="sm"
        >
          <Stack>
            <Stack gap="xs">
              <Text>Are you sure you want to delete this service?</Text>
              <Text>This action cannot be undone.</Text>
            </Stack>
            <Checkbox
              label="Don't ask me this again"
              checked={dontAskAgain}
              onChange={(event) => setDontAskAgain(event.currentTarget.checked)}
            />
            <Group w="100%" justify="space-between">
              <Button 
                variant="light" 
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setServiceToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                color="red" 
                onClick={handleDeleteConfirm}
              >
                Delete
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </>
  );
}

export default ServicesManagement; 